export type QualityLevel = 'low' | 'medium' | 'high';

export interface QualityPreset {
  level: QualityLevel;
  /** Upper bound on devicePixelRatio; dynamic resolution scales below this. */
  maxPixelRatio: number;
  /** Upper bound on internal render pixels (big retina screens would otherwise be fill-rate bound). */
  maxPixels: number;
  minRenderScale: number;
  msaa: number;
  shadows: boolean;
  shadowMapSize: number;
  /** Multiplier on grass/flower density. */
  grassDensity: number;
  grassDistance: number;
  /** Terrain chunks to keep ahead of the player. */
  viewDistance: number;
  /** Volumetric cloud pass. */
  cloudScale: number;
  cloudSteps: number;
  cloudLightSteps: number;
  bloom: boolean;
  /** Ambient particles (pollen/sand) multiplier. */
  particles: number;
  /** Decor density multiplier (trees etc. beyond the track). */
  decor: number;
  speedBlurTaps: number;
  /** Grass LOD rings (Ghost-of-Tsushima style): spacing is for a 48 blades/m² map, scaled per map. */
  grassRings: { spacing: number; inner: number; outer: number; segments: number; tile: number }[];
  flowers: boolean;
  /** Terrain LOD radii (m): beyond each, vertex spacing doubles (1 → 2 → 4 → 8 m). */
  terrainLod: [number, number, number];
  /** Props (trees, rocks…) switch to cheap far meshes beyond this distance. */
  propNear: number;
  /** Props are streamed out to this distance (landmarks/far islands go further). */
  propDistance: number;
  /** Cascaded sun shadows reach this far. */
  shadowDistance: number;
}

export const QUALITY_PRESETS: Record<QualityLevel, QualityPreset> = {
  low: {
    level: 'low',
    maxPixelRatio: 1.25,
    maxPixels: 1_200_000,
    minRenderScale: 0.55,
    msaa: 0,
    shadows: false,
    shadowMapSize: 1024,
    grassDensity: 0.32,
    grassDistance: 70,
    viewDistance: 480,
    cloudScale: 0.3,
    cloudSteps: 18,
    cloudLightSteps: 2,
    bloom: false,
    particles: 0.4,
    decor: 0.6,
    speedBlurTaps: 6,
    grassRings: [
      { spacing: 0.13, inner: 0, outer: 10, segments: 4, tile: 4 },
      { spacing: 0.22, inner: 10, outer: 20, segments: 3, tile: 6 },
      { spacing: 0.34, inner: 20, outer: 32, segments: 2, tile: 10 },
    ],
    flowers: false,
    terrainLod: [70, 170, 340],
    propNear: 60,
    propDistance: 330,
    shadowDistance: 90,
  },
  medium: {
    level: 'medium',
    maxPixelRatio: 1.6,
    maxPixels: 2_200_000,
    minRenderScale: 0.6,
    msaa: 0,
    shadows: true,
    shadowMapSize: 1024,
    grassDensity: 0.6,
    grassDistance: 95,
    viewDistance: 640,
    cloudScale: 0.4,
    cloudSteps: 28,
    cloudLightSteps: 3,
    bloom: true,
    particles: 0.7,
    decor: 0.85,
    speedBlurTaps: 8,
    grassRings: [
      { spacing: 0.095, inner: 0, outer: 11, segments: 5, tile: 4 },
      { spacing: 0.15, inner: 11, outer: 26, segments: 4, tile: 7 },
      { spacing: 0.24, inner: 26, outer: 42, segments: 3, tile: 10 },
      { spacing: 0.36, inner: 42, outer: 58, segments: 2, tile: 16 },
    ],
    flowers: true,
    terrainLod: [90, 210, 420],
    propNear: 85,
    propDistance: 420,
    shadowDistance: 140,
  },
  high: {
    level: 'high',
    maxPixelRatio: 2,
    maxPixels: 3_800_000,
    minRenderScale: 0.65,
    msaa: 4,
    shadows: true,
    shadowMapSize: 2048,
    grassDensity: 1,
    grassDistance: 120,
    viewDistance: 820,
    cloudScale: 0.5,
    cloudSteps: 40,
    cloudLightSteps: 4,
    bloom: true,
    particles: 1,
    decor: 1,
    speedBlurTaps: 10,
    grassRings: [
      { spacing: 0.08, inner: 0, outer: 12, segments: 6, tile: 4 },
      { spacing: 0.12, inner: 12, outer: 28, segments: 5, tile: 7 },
      { spacing: 0.19, inner: 28, outer: 48, segments: 3, tile: 11 },
      { spacing: 0.3, inner: 48, outer: 72, segments: 2, tile: 16 },
    ],
    flowers: true,
    terrainLod: [110, 250, 500],
    propNear: 110,
    propDistance: 520,
    shadowDistance: 190,
  },
};
